/**
 * PlaybookRunResumeManager
 *
 * Headless component (renders null) that restores playbook-run progress after
 * a hard refresh. runStore/activityStore are in-memory only, so a reload drops
 * every live SSE subscription even though the backend run keeps executing.
 * Once the auth context is ready, this asks the backend for runs still in
 * status 'running' and reattaches each via runStore.reconnect, which resumes
 * the live stream (GET /api/playbook-runs/{id}/stream) and re-seeds the same
 * state that powers the migration workspace, the /migrations list/card view,
 * and the header ActivityDropdown.
 *
 * Mount once in AppContent BEFORE <Router> (next to AgentRunResumeManager) so
 * the resumed streams survive route changes.
 */

import { useEffect, useRef } from 'react';
import { useSkillsRequestContext } from '@/services/skills';
import { apiGet } from '@/services/studio/client';
import { normalizePlaybookRunListResponse } from '@/services/migrations/normalize';
import { getRunStore } from '@/stores/studio/runStore';

// Runs stuck 'running' in the DB (e.g. instance killed mid-run before a
// terminal status was persisted) must not be resurrected as live indicators.
// The backend hard-caps a run at PLAYGROUND_RUN_TIMEOUT_SECONDS (500s default);
// anything older than that plus margin is a zombie row, not a run worth
// reattaching to.
const MAX_RESUME_AGE_MS = 15 * 60 * 1000;

function startedWithinResumeWindow(startedAt: string): boolean {
  // SQLite rows serialize as "YYYY-MM-DD HH:MM:SS" (UTC, no zone); Postgres as ISO.
  const iso = startedAt.includes('T') ? startedAt : `${startedAt.replace(' ', 'T')}Z`;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return true; // unparseable — let the stream decide
  return Date.now() - ts <= MAX_RESUME_AGE_MS;
}

export function PlaybookRunResumeManager() {
  const { orgId, userId } = useSkillsRequestContext();
  const resumedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!orgId || !userId) return;
    const contextKey = `${orgId}:${userId}`;
    if (resumedForRef.current === contextKey) return;
    resumedForRef.current = contextKey;

    let cancelled = false;
    void apiGet<unknown>('/api/playbook-runs?status=running&limit=25')
      .then((payload) => {
        if (cancelled) return;
        const { runs } = normalizePlaybookRunListResponse(payload);
        const seenSkills = new Set<string>();
        // List is newest-first; resume at most one run per skill (the store is
        // keyed by skill id, so a second reconnect would just replace the first).
        for (const run of runs) {
          // The run store must be keyed by the frontend skill id, which is the
          // SLUG (normalizePlaybookToSkill sets id = slug) — run.skill_id may
          // fall back to the backend template UUID and would never match the
          // /migrations list / workspace subscriptions. surface_slug is the
          // run's slug snapshot, taken at create_run time.
          const storeKey = run.surface_slug || run.skill_id;
          if (!run.id || !storeKey || seenSkills.has(storeKey)) continue;
          if (!startedWithinResumeWindow(run.started_at)) continue;
          seenSkills.add(storeKey);
          getRunStore(storeKey).getState().reconnect(storeKey, run.id, run.surface_name || 'Playbook');
        }
      })
      .catch((err) => {
        // Non-fatal: worst case the UI just doesn't show resumed progress.
        console.warn('[PlaybookRunResumeManager] failed to restore active runs', err);
        resumedForRef.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [orgId, userId]);

  return null;
}
